import PdfIcon from '@mui/icons-material/PictureAsPdf'
import { Box, IconButton, Typography } from '@mui/material'
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import {
  FetchLicensedDirectorBillingDetailsResponse,
  paymentsApi,
} from '../../../../api/swagger'
import useLoadingContext from '../../../../hooks/useLoadingContext'
import { useLoadingIds } from '../../../../hooks/useLoadingIds'
import { useMountEffect } from '../../../../hooks/useMountEffect'
import { isMobileBrowser } from '../../../../utils/isMobileBrowser'
import EmptyPage from '../../../Elements/EmptyPage'
import LoadingProgress from '../../../Elements/LoadingProgress'
import { PdfViewerSkeleton } from '../../../Skeleton/PdfViewerSkeleton'
import { LcdLicensingFeePDF } from './LcdLicensingFeePDF'
import { useAppSnackbar } from '../../../../hooks/useAppSnackbar'

export const LcdLicensingFeeReport: React.FC = () => {
  const { programKey } = useParams()
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()

  const {
    BillingTab: {
      fetchLicensedDirectorBillingDetails:
        fetchLicensedDirectorBillingDetailsLoadingId,
    },
  } = useLoadingIds()

  const [licensingDetails, setLicensingDetails] =
    useState<FetchLicensedDirectorBillingDetailsResponse>()
  const [hasFetchingError, setHasFetchingError] = useState(false)

  const fetchLicensingDetails = async () => {
    try {
      const response = await paymentsApi.fetchLicensedDirectorBillingDetails({
        programKey: Number(programKey),
      })

      setLicensingDetails(response)
    } catch (e) {
      setHasFetchingError(true)
      await handleError(
        e,
        t(
          'LcdLicensingFeeReport.FetchLicensingDetails.Error',
          'An unknown error occurred fetching license details.'
        )
      )
    }
  }

  const { isLoading, triggerFetch } = useLoadingContext({
    asyncFunction: fetchLicensingDetails,
    loadingId: fetchLicensedDirectorBillingDetailsLoadingId,
  })

  useMountEffect(triggerFetch)

  if (isLoading) {
    return <LoadingProgress />
  }

  if (hasFetchingError || !licensingDetails) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'LcdLicensingFeeReport.ErrorMessage',
              'Could not load PDF report, please try again later.'
            )}
          </Typography>
        }
      />
    )
  }

  const isMobile = isMobileBrowser()

  return (
    <Box display="flex" flexDirection={'column'} height={'70vh'}>
      <Box position="relative" width="100%" height="100%">
        <Box position="absolute" width="100%" height="70%" top={0} zIndex={1}>
          {!isMobile && <PdfViewerSkeleton />}
        </Box>
        <Box position="absolute" width="100%" height="100%" top={0} zIndex={2}>
          {isMobile && (
            <Box
              justifyContent="center"
              alignItems="center"
              display="flex"
              flexDirection="column"
              textAlign="center"
            >
              <Typography>
                {t(
                  'LcdLicensingFeeReport.PreviewNotAvailableMessage',
                  'Preview not available on mobile devices, please download the PDF clicking the button below.'
                )}
              </Typography>
              <PDFDownloadLink document={<></>} fileName="billing.pdf">
                <IconButton color="success">
                  <PdfIcon />
                  <Typography>
                    {t('LcdLicensingFeeReport.DownloadPDF', 'Download PDF')}
                  </Typography>
                </IconButton>
              </PDFDownloadLink>
            </Box>
          )}
          <PDFViewer style={{ flex: 1, width: '100%', height: '100%' }}>
            <LcdLicensingFeePDF licensing={licensingDetails} />
          </PDFViewer>
        </Box>
      </Box>
    </Box>
  )
}
